import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { claraFullAnalysis, claraToneAnalysis, claraParseEmail, getClaraHistory, getClaraShares, markClaraShareRead, getClaraInsights } from '../lib/api';
import { Sparkle, Warning, Phone, CheckCircle, ArrowRight, Copy, Check, Lightning, Star, UsersFour, Clock, ChatTeardropDots, ClockCounterClockwise, EnvelopeSimple, ArrowSquareOut, X, ShareNetwork, PaperPlaneTilt, ChartLineUp, ChatCircleText, TrendUp } from '@phosphor-icons/react';
import { TONE, StatusChip } from './enterprise/ui';
import { toast } from 'sonner';

const WOW_LEVEL_STYLES = {
  LOW: 'bg-blue-500/15 border-blue-500/30 text-blue-500 dark:text-blue-400',
  MID: 'bg-amber-500/15 border-amber-500/30 text-amber-500 dark:text-amber-400',
  HIGH: 'bg-purple-500/15 border-purple-500/30 text-purple-500 dark:text-purple-400',
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(text); setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="copy-btn">
      {copied ? <><Check size={12} weight="bold" /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
  );
}

export default function ClaraTab({ currentUser }) {
  const [emailContent, setEmailContent] = useState('');
  const [borrower, setBorrower] = useState('');
  const [sender, setSender] = useState('');
  const [recipientType, setRecipientType] = useState('');
  const [emailGoal, setEmailGoal] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [shares, setShares] = useState([]);
  const [showShares, setShowShares] = useState(false);
  const [insights, setInsights] = useState(null);
  const [showInsightItems, setShowInsightItems] = useState({});
  const [responseLength, setResponseLength] = useState(() => localStorage.getItem('clara_response_length') || 'standard');
  const [modelChoice, setModelChoice] = useState(() => localStorage.getItem('clara_model') || 'claude');
  const [toneRead, setToneRead] = useState(null);
  const [toneLoading, setToneLoading] = useState(false);
  const [showAddinSetup, setShowAddinSetup] = useState(false);
  const [addinDismissed, setAddinDismissed] = useState(() => localStorage.getItem('clara_addin_banner_dismissed') === '1');
  const textareaRef = useRef(null);

  const MANIFEST_URL = 'https://ehdailyburn.com/outlook-addin/manifest.xml';
  const SETUP_URL = 'https://ehdailyburn.com/outlook-addin/setup.html';

  const copyManifestLink = () => {
    navigator.clipboard.writeText(MANIFEST_URL);
    toast.success('Manifest URL copied');
  };

  const dismissAddinBanner = () => {
    localStorage.setItem('clara_addin_banner_dismissed', '1');
    setAddinDismissed(true);
  };

  const loadHistory = useCallback(async () => {
    try { const h = await getClaraHistory(); setHistory(h); } catch (e) { /* noop */ }
  }, []);

  const loadShares = useCallback(async () => {
    if (!currentUser?.initials) return;
    try { const s = await getClaraShares(currentUser.initials); setShares(s); } catch (e) { /* noop */ }
  }, [currentUser?.initials]);

  const loadInsights = useCallback(async () => {
    try { setInsights(await getClaraInsights()); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadHistory(); loadShares(); loadInsights(); }, [loadHistory, loadShares, loadInsights]);

  const openShare = async (share) => {
    if (!share.read) {
      try { await markClaraShareRead(share.id); loadShares(); } catch(e) { /* noop */ }
    }
    // Load shared content into the input area as a starting point
    setEmailContent(share.content);
    setBorrower(share.borrower || '');
    setShowShares(false);
    toast.success(`Loaded draft from ${share.from_name}`);
  };

  const unreadShareCount = shares.filter(s => !s.read).length;

  const handleAnalyze = async () => {
    if (!emailContent.trim()) { toast.error('Paste or drop an email first'); return; }
    setLoading(true);
    setToneRead(null);
    try {
      // Fire tone analysis (Grok) in parallel with the main draft
      const tonePromise = claraToneAnalysis({ email_content: emailContent, borrower }).catch(() => null);
      const res = await claraFullAnalysis({ email_content: emailContent, borrower, sender, recipient_type: recipientType, email_goal: emailGoal, user_id: currentUser?.id, response_length: responseLength, model: modelChoice });
      setResult(res);
      const tone = await tonePromise;
      if (tone?.analysis) setToneRead(tone.analysis);
      toast.success(`Draft ready (${res?._meta?.model || modelChoice}) · tone read ${tone?.analysis ? 'done' : 'skipped'}`);
      loadHistory();
    } catch (e) { toast.error('Clara had trouble analyzing. Try again.'); }
    finally { setLoading(false); setToneLoading(false); }
  };

  const handleToneOnly = async () => {
    if (!emailContent.trim()) { toast.error('Paste an email first'); return; }
    setToneLoading(true);
    try {
      const r = await claraToneAnalysis({ email_content: emailContent, borrower });
      setToneRead(r?.analysis || null);
    } catch (e) { toast.error('Tone analysis unavailable'); }
    finally { setToneLoading(false); }
  };

  const cleanHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('style, script, link, meta').forEach(el => el.remove());
    const text = doc.body.textContent || '';
    return text.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+/g, ' ').trim();
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    
    // Priority 1: text/plain (cleanest)
    const plain = e.dataTransfer.getData('text/plain');
    if (plain?.trim()) {
      setEmailContent(plain.trim());
      toast.success('Email loaded');
      return;
    }
    
    // Priority 2: text/html (from Outlook web, Gmail)
    const html = e.dataTransfer.getData('text/html');
    if (html?.trim()) {
      const cleaned = cleanHtml(html);
      if (cleaned) {
        setEmailContent(cleaned);
        toast.success('Email loaded');
        return;
      }
    }
    
    // Priority 3: Files — only handle text-based files, skip binary .msg
    if (e.dataTransfer.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      const name = file.name.toLowerCase();
      
      // Skip binary Outlook .msg files
      if (name.endsWith('.msg')) {
        toast.info('Reading Outlook email...');
        const formData = new FormData();
        formData.append('file', file);
        claraParseEmail(formData).then(res => {
          const parts = [];
          if (res.sender) parts.push(`From: ${res.sender}`);
          if (res.subject) parts.push(`Subject: ${res.subject}`);
          if (res.date) parts.push(`Date: ${res.date}`);
          if (parts.length) parts.push('');
          parts.push(res.body || '');
          setEmailContent(parts.join('\n').trim());
          if (res.sender) setSender(res.sender.split('<')[0].trim());
          toast.success('Outlook email loaded');
        }).catch(() => {
          toast.error('Could not read .msg file. Try copy-paste instead.');
        });
        return;
      }
      
      // Handle .eml, .txt, .html files
      if (name.endsWith('.eml')) {
        toast.info('Reading email file...');
        const formData = new FormData();
        formData.append('file', file);
        claraParseEmail(formData).then(res => {
          const parts = [];
          if (res.sender) parts.push(`From: ${res.sender}`);
          if (res.subject) parts.push(`Subject: ${res.subject}`);
          if (res.date) parts.push(`Date: ${res.date}`);
          if (parts.length) parts.push('');
          parts.push(res.body || '');
          setEmailContent(parts.join('\n').trim());
          if (res.sender) setSender(res.sender.split('<')[0].trim());
          toast.success('Email loaded');
        }).catch(() => {
          toast.error('Could not read email file. Try copy-paste instead.');
        });
        return;
      }
      
      if (name.endsWith('.txt') || name.endsWith('.html') || name.endsWith('.htm') || file.type.startsWith('text/')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          let content = ev.target.result || '';
          if (content.includes('<html') || content.includes('<body') || content.includes('<div')) {
            content = cleanHtml(content);
          }
          // For .eml files, extract the body (skip headers)
          if (name.endsWith('.eml')) {
            const bodyStart = content.indexOf('\n\n');
            if (bodyStart > 0) content = content.substring(bodyStart + 2);
          }
          if (content.trim()) {
            setEmailContent(content.trim());
            toast.success('Email loaded');
          } else {
            toast.error('Could not read email content');
          }
        };
        reader.readAsText(file, 'utf-8');
        return;
      }
      
      toast.error('Unsupported file type. Please copy-paste the email text instead.');
      return;
    }
    
    toast.error('No email content detected. Try copy-paste instead.');
  };

  const handlePaste = (e) => {
    // Handle paste with HTML content (copy from email client)
    const html = e.clipboardData?.getData('text/html');
    const plain = e.clipboardData?.getData('text/plain');
    
    // If there's HTML and the textarea is empty, prefer cleaned HTML
    if (html && !emailContent) {
      e.preventDefault();
      const cleaned = cleanHtml(html);
      if (cleaned) setEmailContent(cleaned);
      else if (plain) setEmailContent(plain.trim());
    }
  };

  const handleClear = () => {
    setEmailContent('');
    setBorrower('');
    setSender('');
    setRecipientType('');
    setEmailGoal('');
    setResult(null);
    if (textareaRef.current) textareaRef.current.focus();
  };

  const loadPastAnalysis = (entry) => {
    setEmailContent(entry.email_content || '');
    setBorrower(entry.borrower || '');
    setSender(entry.sender || '');
    setRecipientType(entry.recipient_type || '');
    setEmailGoal(entry.email_goal || '');
    setResult(entry.analysis || null);
    setShowHistory(false);
    toast.success(`Loaded ${entry.borrower} analysis`);
  };

  const toneColor = {
    calm: 'text-emerald-500', concerned: 'text-amber-500', frustrated: 'text-red-500',
    urgent: 'text-orange-500', angry: 'text-red-600',
  };

  return (
    <div className="space-y-5" data-testid="clara-tab">
      {/* Clara AI Insights */}
      {insights?.cards && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="clara-insights-row">
          {insights.cards.map(card => {
            const cfgMap = {
              risk:        { tone: 'critical', icon: Warning, badge: 'RISK ALERT' },
              sentiment:   { tone: 'high',     icon: ChatCircleText, badge: 'CLIENT SENTIMENT' },
              opportunity: { tone: 'good',     icon: Star, badge: 'OPPORTUNITY' },
              team:        { tone: 'ai',       icon: UsersFour, badge: 'TEAM INSIGHT' },
            };
            const cfg = cfgMap[card.type] || cfgMap.team;
            const Icon = cfg.icon;
            const t = TONE[cfg.tone];
            const expanded = showInsightItems[card.id];
            return (
              <div key={card.id} className={`rounded-lg border ${t.border} ${t.bg} p-3 flex flex-col`} data-testid={`insight-${card.type}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-md ${t.bg} border ${t.border} flex items-center justify-center`}>
                    <Icon size={16} weight="fill" className={t.text} />
                  </div>
                  <StatusChip tone={cfg.tone}>{cfg.badge}</StatusChip>
                </div>
                <h3 className="text-sm font-bold text-foreground leading-tight">{card.headline}</h3>
                <p className="text-[11px] text-muted-foreground mt-2 flex-1"><strong className="text-foreground/80">Why it matters:</strong> {card.why}</p>
                <div className="mt-3 pt-2 border-t border-border/50">
                  <p className="text-[11px] font-semibold text-foreground/80">Recommended next action:</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{card.next_action}</p>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                  <span className="text-[10px] text-muted-foreground truncate">Source: {card.source}</span>
                  {card.items?.length > 0 && (
                    <button onClick={() => setShowInsightItems(p => ({ ...p, [card.id]: !p[card.id] }))} className={`text-[11px] font-semibold ${t.text} hover:underline whitespace-nowrap`} data-testid={`insight-view-details-${card.type}`}>
                      {expanded ? 'Hide' : 'View Details'} <ArrowRight size={10} className="inline" />
                    </button>
                  )}
                </div>
                {expanded && card.items?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                    {card.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px]">
                        <span className={`w-1 h-1 rounded-full ${TONE[it.severity]?.dot || TONE.neutral.dot}`} />
                        <span className="font-semibold text-foreground truncate">{it.borrower}</span>
                        <span className="text-muted-foreground truncate flex-1">{it.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Outlook Add-in Setup Banner */}
      {!addinDismissed && (
        <Card className="border border-[#E63935]/30 bg-gradient-to-r from-[#E63935]/10 to-transparent" data-testid="outlook-addin-banner">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-[#E63935]/20 flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={20} weight="fill" className="text-[#E63935]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-foreground">Use Clara directly inside Outlook</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Install the Clara add-in to draft replies without leaving your inbox. Works in Outlook Web and Desktop.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" onClick={() => setShowAddinSetup(!showAddinSetup)}
                      className="h-8 bg-[#E63935] hover:bg-[#d32f2f] text-white text-xs" data-testid="show-addin-setup-btn">
                      {showAddinSetup ? 'Hide Instructions' : 'Show Install Instructions'}
                    </Button>
                    <a href={SETUP_URL} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border text-xs text-muted-foreground hover:text-foreground hover:border-[#E63935]/40 transition-colors"
                      data-testid="open-setup-page-btn">
                      <ArrowSquareOut size={12} /> Open Setup Page
                    </a>
                  </div>
                </div>
              </div>
              <button onClick={dismissAddinBanner} className="text-muted-foreground hover:text-foreground" data-testid="dismiss-addin-banner">
                <X size={16} />
              </button>
            </div>

            {showAddinSetup && (
              <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid="addin-setup-instructions">
                <div className="bg-background/50 rounded-lg p-3 border border-border">
                  <p className="text-xs font-semibold text-foreground mb-2">Manifest URL (copy this):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-card border border-border rounded px-2 py-1.5 text-[#E63935] break-all" data-testid="manifest-url-display">
                      {MANIFEST_URL}
                    </code>
                    <Button size="sm" variant="outline" onClick={copyManifestLink}
                      className="h-8 text-xs border-border" data-testid="copy-manifest-btn">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">Steps to install (Outlook Web):</p>
                  <ol className="list-decimal list-inside space-y-1.5 pl-1">
                    <li>Open <a href="https://outlook.office.com" target="_blank" rel="noopener noreferrer" className="text-[#E63935] hover:underline">outlook.office.com</a> and click any email</li>
                    <li>Click the <strong className="text-foreground">&quot;…&quot;</strong> (more actions) menu at the top of the email</li>
                    <li>Click <strong className="text-foreground">Get Add-ins</strong></li>
                    <li>Click <strong className="text-foreground">My add-ins</strong> in the left sidebar</li>
                    <li>Scroll to <strong className="text-foreground">Custom Addins</strong> → click <strong className="text-foreground">+ Add a custom add-in</strong> → <strong className="text-foreground">Add from URL</strong></li>
                    <li>Paste the manifest URL above and click <strong className="text-foreground">Install</strong></li>
                    <li>A <strong className="text-foreground">&quot;Clara AI&quot;</strong> button appears in your Outlook toolbar</li>
                  </ol>
                </div>

                <div className="space-y-2 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">If &quot;Add from URL&quot; is not available:</p>
                  <ol className="list-decimal list-inside space-y-1.5 pl-1">
                    <li>Open the manifest URL above in a new tab</li>
                    <li>Press <strong className="text-foreground">Ctrl+S</strong> (or <strong className="text-foreground">⌘+S</strong> on Mac) and save as <code className="bg-card px-1 rounded">manifest.xml</code></li>
                    <li>Back in Outlook, choose <strong className="text-foreground">Add from file</strong> instead and select the saved file</li>
                  </ol>
                </div>

                <div className="space-y-2 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">Deploy to entire team (admins only):</p>
                  <ol className="list-decimal list-inside space-y-1.5 pl-1">
                    <li>Go to <a href="https://admin.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-[#E63935] hover:underline">admin.microsoft.com</a></li>
                    <li><strong className="text-foreground">Settings → Integrated apps → Upload custom apps</strong></li>
                    <li>Choose <strong className="text-foreground">Office Add-in → Provide link to manifest file</strong></li>
                    <li>Paste the manifest URL and assign to team members</li>
                  </ol>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <strong>Already tried and got &quot;Add-in failed to load&quot;?</strong> Remove the old install first: <strong>Get Add-ins → My add-ins</strong> → find Clara → click <strong>&quot;…&quot;</strong> → <strong>Remove</strong>. Then re-install with the URL above.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkle size={22} weight="fill" className="text-[#E63935]" /> Email / Message Input
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {(emailContent || result) && (
                    <Button variant="outline" size="sm" onClick={handleClear}
                      className="h-8 text-xs border-border text-muted-foreground hover:text-foreground" data-testid="clara-clear-btn">
                      New Email
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { setShowShares(!showShares); setShowHistory(false); }}
                    className={`h-8 text-xs border-border relative ${showShares ? 'text-[#E63935] border-[#E63935]/30' : 'text-muted-foreground hover:text-foreground'}`} data-testid="clara-shares-btn">
                    <ShareNetwork size={14} className="mr-1" /> Shared with me ({shares.length})
                    {unreadShareCount > 0 && <span className="absolute -top-1 -right-1 bg-[#E63935] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unreadShareCount}</span>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setShowHistory(!showHistory); setShowShares(false); }}
                    className={`h-8 text-xs border-border ${showHistory ? 'text-[#E63935] border-[#E63935]/30' : 'text-muted-foreground hover:text-foreground'}`} data-testid="clara-history-btn">
                    <ClockCounterClockwise size={14} className="mr-1" /> History ({history.length})
                  </Button>
                </div>
              </div>
              {showShares && (
                <div className="mt-3 max-h-72 overflow-y-auto border border-border rounded-lg bg-background/50" data-testid="clara-shares-panel">
                  {shares.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-4 text-center">No drafts shared with you yet. Teammates can send drafts from the Outlook add-in.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {shares.map(s => (
                        <button key={s.id} onClick={() => openShare(s)}
                          className={`w-full text-left p-3 hover:bg-[#E63935]/5 transition-colors ${!s.read ? 'bg-[#E63935]/5' : ''}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <PaperPlaneTilt size={12} className="text-[#E63935] flex-shrink-0" />
                                <p className="text-xs font-semibold text-foreground truncate">{s.from_name}</p>
                                {!s.read && <span className="text-[9px] bg-[#E63935] text-white px-1.5 py-0.5 rounded-full font-bold">NEW</span>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5"><strong>{s.borrower}</strong>{s.note ? ` — ${s.note}` : ''}</p>
                              <p className="text-[10px] text-muted-foreground/70 line-clamp-2 mt-1">{s.content.substring(0, 140)}...</p>
                            </div>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className={`relative rounded-xl border-2 border-dashed transition-colors ${dragOver ? 'border-[#E63935] bg-[#E63935]/5' : 'border-border'}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                onDrop={handleDrop}>
                <Textarea ref={textareaRef} value={emailContent} onChange={(e) => setEmailContent(e.target.value)}
                  onPaste={handlePaste}
                  placeholder="Paste or drag & drop an email here...&#10;&#10;Best methods:&#10;1. Open email in Outlook → Select All (Ctrl+A) → Copy (Ctrl+C) → Paste here (Ctrl+V)&#10;2. Drag .eml or .txt files here&#10;3. Drag text from Outlook Web or Gmail&#10;&#10;Clara will analyze the tone, craft a response, generate loan notes, talking points, and WOW moments."
                  className="border-0 min-h-[200px] text-sm bg-transparent resize-none focus:ring-0"
                  data-testid="clara-email-input" />
                {dragOver && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#E63935]/10 rounded-xl pointer-events-none">
                    <p className="text-[#E63935] font-semibold text-lg">Drop email here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="space-y-3">
          <Card className="border border-border bg-card">
            <CardContent className="p-4 space-y-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Borrower Name</label>
                <Input value={borrower} onChange={(e) => setBorrower(e.target.value)} placeholder="e.g. Johnson" className="h-9 border-border" data-testid="clara-borrower" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Replying To</label>
                <select value={recipientType} onChange={(e) => setRecipientType(e.target.value)}
                  className="w-full h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="clara-recipient-type">
                  <option value="">Select who you are replying to...</option>
                  <option value="Consumer / Borrower">Consumer / Borrower</option>
                  <option value="Realtor Partner">Realtor Partner</option>
                  <option value="VIP Realtor Partner">VIP Realtor Partner</option>
                  <option value="Attorney / Title Company">Attorney / Title Company</option>
                  <option value="Insurance Agent">Insurance Agent</option>
                  <option value="Appraiser">Appraiser</option>
                  <option value="Internal Team Member">Internal Team Member</option>
                  <option value="Underwriter">Underwriter</option>
                  <option value="Other">Other</option>
                </select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Sender / From</label>
                <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="e.g. Name of person who sent email" className="h-9 border-border" data-testid="clara-sender" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Goal / Context</label>
                <Textarea value={emailGoal} onChange={(e) => setEmailGoal(e.target.value)}
                  placeholder="What do you want to accomplish? e.g.&#10;- Calm them down, closing is on track&#10;- Get docs back ASAP without pressure&#10;- Rebuild trust after delay&#10;- Show our value as lending partner&#10;- Protect the relationship"
                  className="border-border min-h-[80px] text-sm" data-testid="clara-goal" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Model</label>
                <div className="flex gap-1 bg-background border border-border rounded-lg p-1" data-testid="clara-model-toggle">
                  {[
                    { v: 'claude', label: 'Claude Opus 5.5', desc: 'Warmest voice' },
                    { v: 'grok',   label: 'Grok 4',          desc: 'Sharper reasoning' },
                    { v: 'gpt',    label: 'GPT-4o',          desc: 'Fastest' },
                  ].map(opt => (
                    <button key={opt.v} type="button"
                      onClick={() => { setModelChoice(opt.v); localStorage.setItem('clara_model', opt.v); }}
                      className={`flex-1 px-2 py-2 rounded-md text-xs font-medium transition-colors flex flex-col items-center gap-0.5 ${modelChoice === opt.v ? 'bg-[color:var(--mr-blue)] text-white' : 'text-muted-foreground hover:bg-muted'}`}
                      data-testid={`clara-model-${opt.v}`}>
                      <span className="font-semibold">{opt.label}</span>
                      <span className="text-[10px] opacity-75">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Response Length</label>
                <div className="flex gap-1 bg-background border border-border rounded-lg p-1" data-testid="clara-length-toggle">
                  {[
                    { v: 'quick', label: 'Quick', desc: '2-4 sentences' },
                    { v: 'standard', label: 'Standard', desc: '5-10 sentences' },
                    { v: 'detailed', label: 'Detailed', desc: 'Rebuild trust' },
                  ].map(opt => (
                    <button key={opt.v} type="button"
                      onClick={() => { setResponseLength(opt.v); localStorage.setItem('clara_response_length', opt.v); }}
                      className={`flex-1 px-2 py-2 rounded-md text-xs font-medium transition-colors flex flex-col items-center gap-0.5 ${responseLength === opt.v ? 'bg-[#E63935] text-white' : 'text-muted-foreground hover:bg-muted'}`}
                      data-testid={`clara-length-${opt.v}`}>
                      <span className="font-semibold">{opt.label}</span>
                      <span className="text-[10px] opacity-75">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAnalyze} disabled={loading || !emailContent.trim()}
                  className="flex-1 h-11 bg-[#E63935] hover:bg-[#d32f2f] text-white text-sm font-semibold" data-testid="clara-analyze-btn">
                  {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" /> Analyzing...</>
                    : <><Sparkle size={16} weight="fill" className="mr-2" /> Analyze & Draft</>}
                </Button>
                <Button onClick={handleToneOnly} disabled={toneLoading || loading || !emailContent.trim()} variant="outline"
                  className="h-11 px-4 border-[color:var(--mr-blue)] text-[color:var(--mr-blue)] text-sm font-semibold" data-testid="clara-tone-btn">
                  {toneLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" /> : 'Tone read'}
                </Button>
              </div>
            </CardContent>
          </Card>
          {result && (
            <Card className={`border-2 ${result.is_frustrated ? 'border-red-500/50 bg-red-500/5' : result.tone_analysis === 'urgent' ? 'border-amber-500/50' : 'border-emerald-500/30'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Tone Detected</p>
                    <p className={`text-lg font-bold capitalize ${toneColor[result.tone_analysis] || 'text-foreground'}`}>{result.tone_analysis || 'Neutral'}</p>
                  </div>
                  {result?._meta?.model && (
                    <span className="text-[10px] font-bold uppercase tracking-wider chip-info border px-2 py-1 rounded" data-testid="draft-model-chip">{result._meta.model}</span>
                  )}
                </div>
                {result.phone_call_recommended && (
                  <div className="mt-2 bg-red-500/15 border border-red-500/40 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="clara-phone-alert">
                    <Phone size={16} weight="fill" className="text-red-500" />
                    <span className="text-red-500 text-xs font-bold uppercase">Phone Call First</span>
                  </div>
                )}
                {result.escalation_needed && (
                  <div className="mt-2 bg-amber-500/15 border border-amber-500/40 rounded-lg px-3 py-2">
                    <span className="text-amber-500 text-xs font-semibold">Escalation: {result.escalation_reason}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {toneRead && (
            <Card className="border-2 border-[color:var(--mr-blue)]/40 bg-[color:var(--mr-blue-wash)]/40" data-testid="clara-tone-panel">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase font-bold tracking-wider text-[color:var(--mr-blue-strong)]">Grok tone read</p>
                    <p className="text-sm font-bold text-foreground">{toneRead.headline || toneRead.primary_tone}</p>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider chip-info border px-2 py-1 rounded">Grok 4</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {toneRead.primary_tone && <span className="chip-critical border text-[11px] font-bold px-2 py-0.5 rounded capitalize">{toneRead.primary_tone}</span>}
                  {(toneRead.secondary_tones || []).slice(0,3).map(t => <span key={t} className="chip-neutral border text-[11px] font-semibold px-2 py-0.5 rounded capitalize">{t}</span>)}
                  {toneRead.call_recommended && <span className="chip-warn border text-[11px] font-bold px-2 py-0.5 rounded">Call recommended</span>}
                </div>
                {toneRead.recommended_next_step && (
                  <div className="bg-card border border-border rounded-md p-2">
                    <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-0.5">Recommended next step</p>
                    <p className="text-xs text-foreground">{toneRead.recommended_next_step}</p>
                  </div>
                )}
                {Array.isArray(toneRead.risk) && toneRead.risk.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1">Risks</p>
                    <div className="space-y-1">
                      {toneRead.risk.map((r, i) => (
                        <div key={i} className="bg-card border border-border rounded-md p-2" data-testid={`tone-risk-${i}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${r.severity === 'high' ? 'chip-critical' : r.severity === 'medium' ? 'chip-warn' : 'chip-info'}`}>{r.severity}</span>
                            <span className="text-[10px] font-semibold text-muted-foreground capitalize">{r.category}</span>
                          </div>
                          <p className="text-xs text-foreground">{r.action}</p>
                          {r.evidence && <p className="text-[10px] italic text-muted-foreground mt-0.5">"{r.evidence}"</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {Array.isArray(toneRead.inferred_subtext) && toneRead.inferred_subtext.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1">What they really mean</p>
                    <div className="space-y-1">
                      {toneRead.inferred_subtext.map((s, i) => (
                        <div key={i} className="bg-card border border-border rounded-md p-2">
                          <p className="text-xs text-foreground">{s.inference}</p>
                          {s.evidence && <p className="text-[10px] italic text-muted-foreground mt-0.5">"{s.evidence}" <span className="not-italic font-semibold">· {s.confidence} confidence</span></p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <Card className="border border-border bg-card" data-testid="clara-history-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-muted-foreground" /> Past Analyses
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No past analyses yet. Analyze an email to start building history.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {history.map(h => (
                  <button key={h.id} onClick={() => loadPastAnalysis(h)}
                    className="w-full text-left p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/50 transition-colors"
                    data-testid={`clara-history-${h.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-foreground">{h.borrower || 'Unknown borrower'}</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleDateString()} {new Date(h.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {h.analysis?.tone_analysis && (
                        <span className={`text-[10px] font-semibold capitalize px-1.5 py-0.5 rounded ${
                          h.analysis.tone_analysis === 'frustrated' || h.analysis.tone_analysis === 'angry' ? 'bg-red-500/15 text-red-500' :
                          h.analysis.tone_analysis === 'concerned' || h.analysis.tone_analysis === 'urgent' ? 'bg-amber-500/15 text-amber-500' :
                          'bg-emerald-500/15 text-emerald-500'
                        }`}>{h.analysis.tone_analysis}</span>
                      )}
                      <span className="text-xs text-muted-foreground truncate">{h.sender || ''}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{(h.email_content || '').slice(0, 100)}...</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-up">
          {/* Crafted Response */}
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Sparkle size={18} className="text-[#E63935]" /> Crafted Response</CardTitle>
                <CopyButton text={result.crafted_response || ''} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-[400px] overflow-y-auto" data-testid="clara-response">
                {result.crafted_response}
              </div>
            </CardContent>
          </Card>

          {/* Realtor Heads Up */}
          {result.realtor_heads_up && !result.realtor_heads_up.toLowerCase().includes('no realtor heads up needed') && (
            <Card className="border-2 border-purple-500/30 bg-card" data-testid="clara-realtor-headsup">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><UsersFour size={18} className="text-purple-500" /> Realtor Heads Up (Send First)</CardTitle>
                  <CopyButton text={result.realtor_heads_up || ''} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[10px] text-purple-500 font-semibold uppercase tracking-wider mb-2">Send this to the Realtor BEFORE contacting the borrower</p>
                <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="clara-realtor-text">
                  {result.realtor_heads_up}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loan Notes */}
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><CheckCircle size={18} className="text-emerald-500" /> Loan Notes</CardTitle>
                <CopyButton text={result.loan_notes || ''} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="clara-notes">
                {result.loan_notes}
              </div>
            </CardContent>
          </Card>

          {/* Talking Points */}
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Lightning size={18} className="text-amber-500" /> Talking Points</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2" data-testid="clara-talking-points">
                {(result.talking_points || []).map((p, i) => (
                  <li key={`tp-${i}`} className="flex items-start gap-2 text-sm">
                    <ArrowRight size={14} weight="bold" className="text-[#E63935] mt-1 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* WOW Moments */}
          <Card className="border border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Star size={18} weight="fill" className="text-purple-500" /> WOW Moment Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="clara-wow-moments">
                {(result.wow_moments || []).map((w, i) => (
                  <div key={`wow-${i}`} className={`rounded-lg border p-3 ${WOW_LEVEL_STYLES[w.level] || WOW_LEVEL_STYLES.LOW}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider">{w.level}</span>
                    </div>
                    <p className="text-sm font-medium">{w.suggestion}</p>
                    {w.reason && <p className="text-xs opacity-80 mt-1">{w.reason}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Follow Up Plan */}
          {result.follow_up_plan && (
            <Card className="border border-border bg-card" data-testid="clara-followup">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Clock size={18} className="text-blue-500" /> Follow Up Plan</CardTitle>
                  <CopyButton text={result.follow_up_plan || ''} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
                  {result.follow_up_plan}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          {result.next_steps?.length > 0 && (
            <Card className="lg:col-span-2 border border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><ArrowRight size={18} className="text-[#E63935]" /> Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="clara-next-steps">
                  {result.next_steps.map((s, i) => (
                    <div key={`ns-${i}`} className="bg-muted rounded-lg px-4 py-2 text-sm flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#E63935] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      {s}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
